import { clamp, median } from '~/lib/utils';
import type { BlobCandidate } from '~/types/pipeline';

export type BlobGridInference = {
  rows: number;
  cols: number;
  rowDividers: number[];
  colDividers: number[];
  confidence: number;
};

function spacingConfidence(centers: readonly number[]): number {
  if (centers.length < 2) return 0.5;
  const spacings: number[] = [];
  for (let i = 1; i < centers.length; i++) {
    spacings.push((centers[i] ?? 0) - (centers[i - 1] ?? 0));
  }
  const mean = spacings.reduce((sum, value) => sum + value, 0) / spacings.length;
  if (mean <= 0) return 0;
  const variance = spacings.reduce((sum, value) => sum + (value - mean) ** 2, 0) / spacings.length;
  return clamp(0, 1, 1 - Math.sqrt(variance) / mean);
}

function cluster1D(values: readonly number[], threshold: number): number[] {
  if (values.length === 0) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const centers: number[] = [];
  let sum = sorted[0] ?? 0;
  let count = 1;

  for (let i = 1; i < sorted.length; i++) {
    const value = sorted[i] ?? 0;
    const previous = sorted[i - 1] ?? 0;
    if (value - previous > threshold) {
      centers.push(sum / count);
      sum = value;
      count = 1;
      continue;
    }
    sum += value;
    count++;
  }
  centers.push(sum / count);
  return centers;
}

function centersToDividers(centers: readonly number[], minEdge: number, maxEdge: number): number[] {
  if (centers.length === 0) return [Math.round(minEdge), Math.round(maxEdge)];
  const sortedCenters = [...centers].sort((a, b) => a - b);
  const dividers = [Math.round(minEdge)];
  for (let i = 1; i < sortedCenters.length; i++) {
    dividers.push(Math.round(((sortedCenters[i - 1] ?? 0) + (sortedCenters[i] ?? 0)) / 2));
  }
  dividers.push(Math.round(maxEdge));
  return dividers;
}

export function inferGridFromBlobs(blobs: readonly BlobCandidate[]): BlobGridInference | null {
  if (blobs.length === 0) return null;

  const widths = blobs.map((blob) => blob.bbox.w);
  const heights = blobs.map((blob) => blob.bbox.h);
  const xCenters = blobs.map((blob) => blob.centroid.x);
  const yCenters = blobs.map((blob) => blob.centroid.y);

  const xThreshold = Math.max(1, median(widths) * 0.6);
  const yThreshold = Math.max(1, median(heights) * 0.6);
  const colCenters = cluster1D(xCenters, xThreshold);
  const rowCenters = cluster1D(yCenters, yThreshold);

  const cols = Math.max(1, colCenters.length);
  const rows = Math.max(1, rowCenters.length);

  const minX = Math.min(...blobs.map((blob) => blob.bbox.x));
  const minY = Math.min(...blobs.map((blob) => blob.bbox.y));
  const maxX = Math.max(...blobs.map((blob) => blob.bbox.x + blob.bbox.w));
  const maxY = Math.max(...blobs.map((blob) => blob.bbox.y + blob.bbox.h));

  const expected = rows * cols;
  const coverage = expected > 0 ? Math.min(1, blobs.length / expected) : 0;
  const regularity = (spacingConfidence(colCenters) + spacingConfidence(rowCenters)) / 2;
  const confidence = clamp(0, 1, coverage * regularity);

  return {
    rows,
    cols,
    rowDividers: centersToDividers(rowCenters, minY, maxY),
    colDividers: centersToDividers(colCenters, minX, maxX),
    confidence,
  };
}
